import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  PermissionMatrixDocument,
  type PermissionMatrix,
  SetRolePermissionDocument,
} from "#app/lib/data";
import type { CellState } from "@salestrak/permissions";

// Apollo plumbing for the Permissions page's "Custom Role
// Permissions" matrix. Returns the matrix payload + a setCell helper
// that writes optimistically so cell toggles render instantly.

export type { PermissionMatrix };

export function usePermissionMatrix(workspaceId: string) {
  const queryOptions = useMemo(
    () => ({ variables: { workspaceId }, skip: !workspaceId }),
    [workspaceId],
  );
  const { data, loading } = useQuery(
    PermissionMatrixDocument,
    queryOptions,
  );
  const [setRolePermission] = useMutation(SetRolePermissionDocument);

  const setCell = useCallback(
    (roleId: string, permissionKey: string, nextState: CellState) => {
      if (!workspaceId) return;
      void setRolePermission({
        variables: {
          input: { roleId, permissionKey, state: nextState as never },
        },
        optimisticResponse: { setRolePermission: true },
        update: (cache) => {
          const existing = cache.readQuery({
            query: PermissionMatrixDocument,
            variables: { workspaceId },
          });
          if (!existing) return;
          const cells = existing.permissionMatrix.cells.map((c) =>
            c.roleId === roleId && c.permissionKey === permissionKey
              ? { ...c, state: nextState }
              : c,
          );
          cache.writeQuery({
            query: PermissionMatrixDocument,
            variables: { workspaceId },
            data: {
              ...existing,
              permissionMatrix: { ...existing.permissionMatrix, cells },
            },
          });
        },
      });
    },
    [workspaceId, setRolePermission],
  );

  return {
    loading,
    matrix: data?.permissionMatrix as PermissionMatrix | undefined,
    setCell,
  };
}
